import { useEffect, useRef, useState } from 'react'
import { CreditCard, ExternalLink, Trash2, Ban, Plus, Smartphone, Download, Upload, Send, Pencil, X } from 'lucide-react'
import { useCardsStore, type Card } from '../stores/supabaseCardsStore'

// Admin-Page: Mitglieder-Karten ausstellen. Jede Karte hat einen öffentlichen
// Pass unter /pass/:serial (branded Karte + QR + Google-Wallet-Button).
//
// Bedienkonzept (bewusst schlank):
// - "+ Hinzufügen" öffnet Zeilen-Felder (Name/E-Mail) oder eine CSV lädt sie vor.
// - "Speichern" legt Karte + Foxi-Ticket an, OHNE Mail. "Speichern & Senden"
//   verschickt zusätzlich — Versand ist überall ein bewusster, separater Klick.
// - Pro Zeile nur "Senden" + "Bearbeiten"; alles Weitere (QR, Foxi, Sperren,
//   Löschen) lebt im aufgeklappten Bearbeiten-Panel.

const GREEN = '#1B7E3A'

const inputStyle = {
  padding: '9px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 10,
  fontSize: 15,
  fontFamily: 'inherit',
} as const

const btn = (variant: 'primary' | 'outline' | 'danger' | 'plain') =>
  ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 14px',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
    ...(variant === 'primary' && { background: GREEN, color: '#fff', border: `1px solid ${GREEN}` }),
    ...(variant === 'outline' && { background: '#fff', color: GREEN, border: `1px solid ${GREEN}` }),
    ...(variant === 'danger' && { background: '#fff', color: '#b91c1c', border: '1px solid #ef4444' }),
    ...(variant === 'plain' && { background: '#fff', color: '#374151', border: '1px solid #d1d5db' }),
  }) as const

interface DraftRow {
  name: string
  email: string
}

// CSV/Text: eine Zeile = Name;E-Mail (auch Komma oder Tab). Kopfzeile wird erkannt.
function parseRows(text: string): DraftRow[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l, i) => !(i === 0 && /name/i.test(l) && /mail/i.test(l)))
    .map((l) => {
      const [name = '', email = ''] = l.split(/[;,\t]/).map((p) => p.trim().replace(/^"|"$/g, ''))
      return { name, email }
    })
    .filter((r) => r.name)
}

export default function KartenPage() {
  const { cards, loading, error, fetchCards, addCard, updateCard, revokeCard, deleteCard, createFoxi, exportCsv } =
    useCardsStore()

  // Hinzufügen-Panel
  const [adding, setAdding] = useState(false)
  const [rows, setRows] = useState<DraftRow[]>([{ name: '', email: '' }])
  const [season, setSeason] = useState('2025/26')
  const fileRef = useRef<HTMLInputElement>(null)

  // Bearbeiten-Panel (eine Karte gleichzeitig)
  const [editId, setEditId] = useState<string | null>(null)
  const [edit, setEdit] = useState<DraftRow & { season: string }>({ name: '', email: '', season: '' })

  const [busy, setBusy] = useState<string | null>(null) // 'add' | 'edit' | card.id (senden)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    void fetchCards()
  }, [fetchCards])

  const note = (m: string | null, e: string | null) => {
    setMsg(m)
    setErr(e)
  }

  // --- Hinzufügen ---

  const onUpload = async (file: File) => {
    const parsed = parseRows(await file.text())
    if (parsed.length === 0) return note(null, 'Keine Zeilen erkannt. Erwartet: Name;E-Mail pro Zeile.')
    setRows(parsed)
    note(`${parsed.length} Zeilen aus ${file.name} übernommen — prüfen und speichern.`, null)
  }

  const saveAll = async (send: boolean) => {
    const valid = rows.filter((r) => r.name.trim())
    if (valid.length === 0) return note(null, 'Mindestens einen Namen eintragen.')
    setBusy('add')
    note(null, null)
    let ok = 0
    let sent = 0
    const problems: string[] = []
    for (const r of valid) {
      const res = await addCard(r.name, season, r.email)
      if (res.error || !res.id) {
        problems.push(`${r.name}: ${res.error ?? 'unbekannter Fehler'}`)
        continue
      }
      ok++
      const wantMail = send && !!r.email.trim()
      const foxi = await createFoxi(res.id, wantMail)
      if (foxi.error) problems.push(`${r.name}: Karte gespeichert, Foxi-Ticket fehlgeschlagen (${foxi.error})`)
      else if (wantMail) sent++
    }
    setBusy(null)
    if (problems.length) note(null, problems.join(' · '))
    else {
      note(`${ok} Karte${ok === 1 ? '' : 'n'} gespeichert${send ? `, ${sent} verschickt` : ''}.`, null)
      setRows([{ name: '', email: '' }])
      setAdding(false)
    }
  }

  // --- Bearbeiten ---

  const openEdit = (c: Card) => {
    setEditId(c.id)
    setEdit({ name: c.holder_name, email: c.email ?? '', season: c.season ?? '' })
    note(null, null)
  }

  const saveEdit = async (resend: boolean) => {
    if (!editId) return
    setBusy('edit')
    note(null, null)
    const res = await updateCard(editId, { holder_name: edit.name, season: edit.season, email: edit.email })
    if (res.error) {
      setBusy(null)
      return note(null, res.error)
    }
    if (resend) {
      if (!edit.email.trim()) {
        setBusy(null)
        return note('Gespeichert — Senden übersprungen (keine E-Mail).', null)
      }
      const foxi = await createFoxi(editId, true)
      setBusy(null)
      if (foxi.error) return note(null, `Gespeichert, Versand fehlgeschlagen: ${foxi.error}`)
      note(foxi.guardBlocked ? 'Gespeichert — Mailversand in Testumgebung blockiert.' : 'Gespeichert und verschickt.', null)
    } else {
      setBusy(null)
      note('Gespeichert.', null)
    }
    setEditId(null)
  }

  const removeCard = async (c: Card) => {
    if (!confirm(`Karte von ${c.holder_name} löschen?`)) return
    const res = await deleteCard(c.id)
    if (res.error) note(null, res.error)
    else {
      note('Karte gelöscht.', null)
      setEditId(null)
    }
  }

  // --- Senden (Zeile) ---

  const sendCard = async (c: Card) => {
    if (!c.email) return note(null, `${c.holder_name} hat keine E-Mail-Adresse — über Bearbeiten ergänzen.`)
    setBusy(c.id)
    note(null, null)
    const res = await createFoxi(c.id, true)
    setBusy(null)
    if (res.error) note(null, res.error)
    else if (res.guardBlocked) note('Ticket erstellt — Mailversand in Testumgebung blockiert.', null)
    else note(`Ticket an ${c.email} verschickt.`, null)
  }

  const badge = (bg: string, fg: string, label: string) => (
    <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  )

  return (
    <div style={{ padding: '24px 0', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, margin: '0 0 6px' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 28, fontWeight: 800, color: '#111', margin: 0, flex: 1 }}>
          <CreditCard size={26} color={GREEN} /> Mitglieder-Karten
        </h1>
        <button onClick={exportCsv} title="CSV für Passcreator-Massenverarbeitung" style={btn('plain')}>
          <Download size={15} /> CSV
        </button>
        <button
          onClick={() => {
            setAdding(!adding)
            note(null, null)
          }}
          style={btn(adding ? 'outline' : 'primary')}
        >
          {adding ? <X size={16} /> : <Plus size={16} />} {adding ? 'Abbrechen' : 'Hinzufügen'}
        </button>
      </div>
      <p style={{ color: '#555', margin: '0 0 20px', fontSize: 14 }}>
        Speichern legt Karte + Foxi-Wallet-Ticket an. Mail geht nur bei «Senden» raus.
      </p>

      {msg && <p style={{ color: GREEN, fontWeight: 600, margin: '0 0 14px' }}>✓ {msg}</p>}
      {err && <p style={{ color: '#c8102e', fontWeight: 600, margin: '0 0 14px' }}>{err}</p>}
      {error && <p style={{ color: '#c8102e', margin: '0 0 14px' }}>{error}</p>}

      {/* Hinzufügen */}
      {adding && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20, marginBottom: 28, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
            <button onClick={() => fileRef.current?.click()} style={btn('plain')} title="CSV/Text: eine Zeile = Name;E-Mail">
              <Upload size={15} /> Datei laden
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.txt"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void onUpload(f)
                e.target.value = ''
              }}
            />
            <span style={{ fontSize: 13, color: '#6b7280' }}>oder Felder direkt ausfüllen</span>
            <span style={{ flex: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#6b7280' }}>
              Saison
              <input value={season} onChange={(e) => setSeason(e.target.value)} style={{ ...inputStyle, width: 90, padding: '6px 10px', fontSize: 14 }} />
            </label>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            {rows.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  value={r.name}
                  onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  placeholder="Max Muster"
                  style={{ ...inputStyle, flex: '1 1 180px' }}
                />
                <input
                  value={r.email}
                  onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                  type="email"
                  placeholder="max@example.ch (optional)"
                  style={{ ...inputStyle, flex: '1 1 220px' }}
                />
                {rows.length > 1 && (
                  <button
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}
                    title="Zeile entfernen"
                    style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4, lineHeight: 0 }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 14 }}>
            <button onClick={() => setRows([...rows, { name: '', email: '' }])} style={{ ...btn('plain'), padding: '7px 12px', fontSize: 13 }}>
              <Plus size={14} /> Zeile
            </button>
            <span style={{ flex: 1 }} />
            <button onClick={() => void saveAll(false)} disabled={busy === 'add'} style={{ ...btn('outline'), opacity: busy === 'add' ? 0.6 : 1 }}>
              {busy === 'add' ? 'Speichere…' : 'Speichern'}
            </button>
            <button onClick={() => void saveAll(true)} disabled={busy === 'add'} style={{ ...btn('primary'), opacity: busy === 'add' ? 0.6 : 1 }}>
              <Send size={15} /> {busy === 'add' ? 'Speichere…' : 'Speichern & Senden'}
            </button>
          </div>
        </div>
      )}

      {/* Liste */}
      <h2 style={{ fontSize: 18, fontWeight: 800, color: '#111', margin: '0 0 12px' }}>Ausgestellte Karten ({cards.length})</h2>
      {loading && cards.length === 0 ? (
        <p style={{ color: '#666' }}>Lädt…</p>
      ) : cards.length === 0 ? (
        <div style={{ border: '1.5px dashed #d1d5db', borderRadius: 12, padding: '40px 16px', textAlign: 'center', color: '#777' }}>
          🪪 Noch keine Karten.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {cards.map((c) =>
            editId === c.id ? (
              /* Bearbeiten-Panel */
              <div key={c.id} style={{ background: '#fff', border: `1.5px solid ${GREEN}`, borderRadius: 12, padding: '16px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <input
                    value={edit.name}
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                    placeholder="Name"
                    style={{ ...inputStyle, flex: '1 1 180px' }}
                  />
                  <input
                    value={edit.email}
                    onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                    type="email"
                    placeholder="E-Mail (optional)"
                    style={{ ...inputStyle, flex: '1 1 220px' }}
                  />
                  <input
                    value={edit.season}
                    onChange={(e) => setEdit({ ...edit, season: e.target.value })}
                    placeholder="Saison"
                    style={{ ...inputStyle, flex: '0 1 110px' }}
                  />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                  <a href={`/pass/${encodeURIComponent(c.serial)}`} target="_blank" rel="noreferrer" style={{ ...btn('plain'), padding: '7px 12px', fontSize: 13 }}>
                    <ExternalLink size={14} /> QR
                  </a>
                  {c.foxi_url && (
                    <a href={c.foxi_url} target="_blank" rel="noreferrer" style={{ ...btn('plain'), padding: '7px 12px', fontSize: 13 }}>
                      <Smartphone size={14} /> Foxi
                    </a>
                  )}
                  {c.status === 'active' && (
                    <button
                      onClick={() => void revokeCard(c.id).then((r) => note(r.error ? null : 'Karte gesperrt.', r.error ?? null))}
                      style={{ ...btn('plain'), padding: '7px 12px', fontSize: 13, color: '#b45309', borderColor: '#f59e0b' }}
                    >
                      <Ban size={14} /> Sperren
                    </button>
                  )}
                  <button onClick={() => void removeCard(c)} style={{ ...btn('danger'), padding: '7px 12px', fontSize: 13 }}>
                    <Trash2 size={14} /> Löschen
                  </button>
                  <span style={{ flex: 1 }} />
                  <button onClick={() => setEditId(null)} style={btn('plain')}>
                    Abbrechen
                  </button>
                  <button onClick={() => void saveEdit(false)} disabled={busy === 'edit'} style={{ ...btn('outline'), opacity: busy === 'edit' ? 0.6 : 1 }}>
                    {busy === 'edit' ? 'Speichere…' : 'Speichern'}
                  </button>
                  <button onClick={() => void saveEdit(true)} disabled={busy === 'edit'} style={{ ...btn('primary'), opacity: busy === 'edit' ? 0.6 : 1 }}>
                    <Send size={15} /> Speichern & erneut senden
                  </button>
                </div>
              </div>
            ) : (
              /* Normale Zeile */
              <div key={c.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 16px' }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#111' }}>{c.holder_name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    {c.email ?? 'keine E-Mail'} · Saison {c.season ?? '—'}
                  </div>
                </div>
                {c.status !== 'active'
                  ? badge('#fee2e2', '#991b1b', 'gesperrt')
                  : c.sent_at
                    ? badge('#dcfce7', '#166534', '✓ verschickt')
                    : badge('#f3f4f6', '#374151', 'aktiv')}
                <button
                  onClick={() => void sendCard(c)}
                  disabled={busy === c.id || c.status !== 'active'}
                  title={c.email ? `Wallet-Ticket per Mail an ${c.email}` : 'Keine E-Mail hinterlegt'}
                  style={{ ...btn('primary'), padding: '8px 12px', fontSize: 13, opacity: busy === c.id || c.status !== 'active' ? 0.5 : 1, cursor: busy === c.id ? 'wait' : c.status !== 'active' ? 'not-allowed' : 'pointer' }}
                >
                  <Send size={14} /> {busy === c.id ? 'Sende…' : c.sent_at ? 'Erneut senden' : 'Senden'}
                </button>
                <button onClick={() => openEdit(c)} style={{ ...btn('plain'), padding: '8px 12px', fontSize: 13 }}>
                  <Pencil size={14} /> Bearbeiten
                </button>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  )
}
